import { ReactNode } from 'react';

import { Icons, Stack } from '@cast/design-system';
import type { MetricsBoxProps } from '@cast/design-system';

import { InventoryResourceMetricBox } from './InventoryResourceMetricBox';

type InventoryResourceMetricBoxPairProps<TKey extends string> = {
  testId?: string;
  currentDataKey: TKey;
  currentValue: number | null | undefined;
  recommendedDataKey: TKey;
  recommendedValue: number | null | undefined;
  unit?: ReactNode;
  colorMap: Record<TKey, string>;
  titleMap: Record<TKey, string>;
  toggleMetric?: (key: TKey) => void;
  isMetricDisabled?: (key: TKey) => boolean;
  size?: MetricsBoxProps['size'];
  alignItems?: 'stretch' | 'center' | 'flex-start' | 'flex-end';
  gap?: number;
  arrowColor?: string;
  arrowWeight?: React.ComponentProps<typeof Icons.ArrowRight>['weight'];
};

export const InventoryResourceMetricBoxPair = <TKey extends string>({
  testId,
  currentDataKey,
  currentValue,
  recommendedDataKey,
  recommendedValue,
  unit,
  colorMap,
  titleMap,
  toggleMetric,
  isMetricDisabled,
  size,
  alignItems = 'center',
  gap = 16,
  arrowColor,
  arrowWeight = 'bold',
}: InventoryResourceMetricBoxPairProps<TKey>) => (
  <Stack direction="row" alignItems={alignItems} gap={gap} data-testid={testId}>
    <InventoryResourceMetricBox
      dataKey={currentDataKey}
      value={currentValue}
      unit={unit}
      testId="current"
      colorMap={colorMap}
      titleMap={titleMap}
      toggleMetric={toggleMetric}
      isMetricDisabled={isMetricDisabled}
      size={size}
    />

    <Icons.ArrowRight size={16} weight={arrowWeight} color={arrowColor} />

    <InventoryResourceMetricBox
      dataKey={recommendedDataKey}
      value={recommendedValue}
      unit={unit}
      testId="recommended"
      colorMap={colorMap}
      titleMap={titleMap}
      toggleMetric={toggleMetric}
      isMetricDisabled={isMetricDisabled}
      size={size}
    />
  </Stack>
);
